import { useState, useRef, useCallback, useMemo } from 'react';
import type { UIMessage } from 'ai';
import { Flex, IconButton, Tooltip, iconWeight, Icon, message } from '@nula/core-ui';
import { SidePanel } from '@/components/side-panel/side-panel';
import { useUIStore } from '@/stores/use-ui-store';
import { useActiveEntityId } from '@/hooks/navigation';
import { useAssistantChat } from '../hooks/use-assistant-chat';
import { useCancelRun } from '../hooks/use-cancel-run';
import { useAssistantContext } from '../hooks/use-assistant-context';
import { useSubmitFeedback } from '../hooks/use-submit-feedback';
import { AssistantMessageList } from './assistant-message-list';
import { AssistantInput } from './assistant-input';
import { FeedbackModal } from './messages/feedback-modal';
import { useAssistantChatContext } from './assistant-chat-provider';
import type { FeedbackVote } from './messages/message-toolbar';
import styles from './assistant-sidebar.module.css';

interface AssistantSidebarProps {
  onClose: () => void;
}

/**
 * Find the approvalId of the first approval-requested tool part across all messages.
 *
 * Duplicated verbatim in `AssistantOverlay.tsx` and `AssistantFullTab.tsx` per the
 * M12b plan's small-diff rule. Extract to a shared hook in a future cleanup.
 */
function findPendingApprovalId(messages: UIMessage[]): string | null {
  for (const msg of messages) {
    if (msg.role !== 'assistant') continue;
    for (const part of msg.parts ?? []) {
      if (
        (part.type.startsWith('tool-') || part.type === 'dynamic-tool') &&
        (part as { state?: string }).state === 'approval-requested'
      ) {
        const approvalId = (part as { approval?: { id?: string } }).approval?.id;
        if (approvalId) return approvalId;
      }
    }
  }
  return null;
}

export function AssistantSidebar({ onClose }: AssistantSidebarProps) {
  const activeEntityId = useActiveEntityId();

  const assistantContext = useAssistantContext(activeEntityId);
  const { startNewConversation } = useAssistantChatContext();

  const chat = useAssistantChat();
  const { cancel } = useCancelRun();
  const { submit: submitFeedback } = useSubmitFeedback();
  const [feedbackState, setFeedbackState] = useState<{
    open: boolean;
    vote: FeedbackVote;
    runId: string | undefined;
  }>({ open: false, vote: 'up', runId: undefined });

  const handleVote = useCallback(
    (vote: FeedbackVote, runId: string | undefined) => {
      setFeedbackState({ open: true, vote, runId });
    },
    [],
  );

  const handleFeedbackSubmit = useCallback(
    async (comment: string) => {
      const { submitted } = await submitFeedback({
        runId: feedbackState.runId ?? '',
        vote: feedbackState.vote,
        comment,
      });
      if (submitted) {
        message.success('Thanks — feedback sent');
      } else {
        message.error(
          'Could not send feedback. Please try again in a moment.',
        );
      }
    },
    [feedbackState.runId, feedbackState.vote, submitFeedback],
  );

  const isRunActive = chat.status === 'streaming' || chat.status === 'submitted';
  const pendingApprovalId = useMemo(() => findPendingApprovalId(chat.messages), [chat.messages]);

  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isRunActive) return;
    setInput('');
    chat.sendMessage({ text });
  }, [input, isRunActive, chat]);

  const handleStop = useCallback(() => {
    if (chat.runId) {
      try { cancel(chat.runId); } catch { /* not connected */ }
    }
    void chat.stop();
  }, [chat, cancel]);

  const handleApprove = useCallback((approvalId: string) => {
    chat.addToolApprovalResponse({ id: approvalId, approved: true });
  }, [chat]);

  const handleCancel = useCallback((approvalId: string) => {
    chat.addToolApprovalResponse({ id: approvalId, approved: false });
  }, [chat]);

  return (
    <SidePanel
      title="Nula AI"
      icon="auto_awesome"
      onClose={onClose}
      bodyClassName={styles.body}
      titleExtra={
        <Flex align="center" gap="xs">
          <Tooltip title="Conversation history" placement="bottom">
            <IconButton
              size="sm"
              aria-label="Conversation history"
              onClick={() => useUIStore.getState().setAssistantHistoryOpen(true)}
              className={styles.headerBtn}
            >
              <Icon name="history" size={14} weight={iconWeight.light} />
            </IconButton>
          </Tooltip>
          <Tooltip title="New conversation" placement="bottom">
            <IconButton size="sm" aria-label="New conversation" onClick={startNewConversation} className={styles.headerBtn}>
              <Icon name="edit_square" size={14} weight={iconWeight.light} />
            </IconButton>
          </Tooltip>
        </Flex>
      }
    >
      {/* Messages */}
      <AssistantMessageList
        messages={chat.messages}
        onApprove={handleApprove}
        onCancel={handleCancel}
        pendingApprovalId={pendingApprovalId}
        onSuggestionClick={(text) => {
          setInput(text);
          inputRef.current?.focus();
        }}
        onVote={handleVote}
        className={styles.messageList}
      />

      {/* Input + context */}
      <AssistantInput
        ref={inputRef}
        className={styles.inputArea}
        value={input}
        onChange={setInput}
        onSend={handleSend}
        onStop={handleStop}
        isRunActive={isRunActive}
        assistantContext={assistantContext}
      />

      <FeedbackModal
        open={feedbackState.open}
        vote={feedbackState.vote}
        onClose={() => setFeedbackState((s) => ({ ...s, open: false }))}
        onSubmit={handleFeedbackSubmit}
      />
    </SidePanel>
  );
}
